import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Alert, Snackbar } from '@mui/material';
import { zIndex } from '../../theme/tokens';

const ToastContext = createContext(null);

const DEFAULT_DURATION = 5000;

let toastApi = null;

export function setToastApi(api) {
  toastApi = api;
}

/** Imperative API for use outside React components (e.g. module-level handlers). */
export const toast = {
  show: (message, options) => toastApi?.show(message, options),
  success: (message, options) => toastApi?.success(message, options),
  error: (message, options) => toastApi?.error(message, options),
  warning: (message, options) => toastApi?.warning(message, options),
  info: (message, options) => toastApi?.info(message, options),
};

export function ToastProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    message: '',
    severity: 'info',
    duration: DEFAULT_DURATION,
  });

  const showToast = useCallback((message, options = {}) => {
    setState({
      open: true,
      message: String(message),
      severity: options.severity || 'info',
      duration: options.duration ?? DEFAULT_DURATION,
    });
  }, []);

  const hideToast = useCallback((_event, reason) => {
    if (reason === 'clickaway') return;
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const api = useMemo(
    () => ({
      show: showToast,
      success: (message, options) => showToast(message, { ...options, severity: 'success' }),
      error: (message, options) => showToast(message, { ...options, severity: 'error' }),
      warning: (message, options) => showToast(message, { ...options, severity: 'warning' }),
      info: (message, options) => showToast(message, { ...options, severity: 'info' }),
    }),
    [showToast]
  );

  useEffect(() => {
    setToastApi(api);
    return () => setToastApi(null);
  }, [api]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={state.duration}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: zIndex.toast }}
      >
        <Alert
          onClose={hideToast}
          severity={state.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: 480, whiteSpace: 'pre-line' }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
